import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from 'sonner'

const TEMPLATES_QUERY_KEY = 'templates'

export interface Template {
  id: string
  name: string
  slug: string
  description?: string
  engine: string
  recipe: string
  is_default: boolean
  is_active: boolean
  customizable_fields: string[]
  created_by: string
  created_at?: string
  updated_at?: string
}

export interface TemplateDetail extends Template {
  html_content?: string
  chrome_options?: Record<string, unknown>
  embedded_assets_keys: string[]
}

export const useTemplates = () => {
  return useQuery<{ templates: Template[]; total: number }>({
    queryKey: [TEMPLATES_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get('/templates')
      return data
    },
  })
}

export const useTemplate = (id: string) => {
  return useQuery<TemplateDetail>({
    queryKey: [TEMPLATES_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/templates/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export interface CreateTemplateRequest {
  name: string
  slug: string
  description?: string
  engine?: string
  recipe?: string
  html_content?: string
}

export const useCreateTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateTemplateRequest) => {
      const { data } = await api.post('/templates', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY] })
      toast.success('Template creado')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg = axiosErr.response?.data?.detail || error.message || 'Error al crear template'
      toast.error(typeof msg === 'string' ? msg : 'Error al crear template')
    },
  })
}

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TemplateDetail> }) => {
      const { data } = await api.patch(`/templates/${id}`, updates)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY] })
      toast.success('Template actualizado')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al actualizar template')
    },
  })
}

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/templates/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY] })
      toast.success('Template eliminado')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: { message?: string } } } }
      const msg =
        axiosErr.response?.data?.detail?.message || error.message || 'Error al eliminar template'
      toast.error(msg)
    },
  })
}

export const useSetDefaultTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/templates/${id}/set-default`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY] })
      toast.success('Template predeterminado actualizado')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al establecer template predeterminado')
    },
  })
}

export const useUploadTemplateAsset = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, assetKey, file }: { id: string; assetKey: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post(
        `/templates/${id}/upload-asset?asset_key=${assetKey}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY] })
      toast.success('Asset subido exitosamente')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al subir asset')
    },
  })
}

export const usePreviewTemplate = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/templates/${id}/preview`)
      return data as { html: string }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al generar preview')
    },
  })
}
